/**
 *
 * This component emits 4 events. One when searching for similarities. Another one for when all the results have been fetched,
 * another event when the resource has been selected, and a last one when the resource has been cleared (x) or one item has been removed from the selection list.
 *
 */

const he = require('he');

const onSelectEvent = "onSelect";
const onRemoveEvent = "onRemove";
const onSearchEvent = "onSearch";
const onFetchEvent = "onFetch";
const onChangeEvent = "onChange";
const onFetchErrorEvent = "onFetchError";

class ResourcePicker{
  constructor($scope, $q, sriClient, $templateRequest){
    'ngInject';
    this.$scope = $scope;
    this.$q = $q;
    this.sriClient = sriClient;
    this.$templateRequest = $templateRequest;

    this.httpRequestCanceler = null;

  }

  async $onInit() {
    if(!this.multiple && !this.resourceListUrl) {console.log('No url parameter?');}
    if(!this.display) {console.log('No display parameter?');}
    if(!this.allowClear) { this.allowClear = false; } // need to force false when value is undefined to keep default in false
    if(this.api) this.api.clear = () => {
      if(this.resource || this.selected.length > 0){
        this.resource = null;
        this.selected.length = 0;
        this.onChangeEmitter(this.multiple ? [] : null);
      }
    };

    this.resource = null;
    this.selected = [];
    this.limit = this.limit || 25;
    this.queryParam = this.queryParam || 'q';
    this.minimumInputLength = this.minimumInputLength || 0;

    if(this.model && !this.multiple) {
      this.resource = this.model;
    }
    else if(this.model && this.multiple) {
      this.selected = this.model;
    }

    if (this.optionsTemplateUrl) {
      const html = await this.$templateRequest(this.optionsTemplateUrl);
      this.template = html;
    }

    //if display is a dotted propertyPath, change display to a function that properly returns the given property
    if(this.display){
      if(angular.isFunction(this.display)){
        this.displayFunction = this.display;
      }else{
        const props = this.display.split('.');
        this.displayFunction = (_resource) => {
          const resource = props.reduce((preV, currV) => {
            return (preV != null && preV.hasOwnProperty(currV)) ? preV[currV] : null;
          }, _resource);

          return resource;
        };
      }
    }
  };

  // if the resource has a special character it is decoded.
  // If the component uses a template, this decoding must be done in that template.
  displayFunctionParser(resource) {
    if (!resource || !this.displayFunction) return null;
    return he.decode(this.displayFunction(resource));
  }

  async $onChanges(bindings) {
    if(bindings.localData) {
      this.localData = bindings.localData.currentValue;
      this.similarResources = this.localData;
    }

    if(bindings.model){
      if(this.isHref(bindings.model.currentValue)){
        const item = await this.sriClient.getRaw(bindings.model.currentValue);
        this.resource = item;
        this.onSelectEmitter(item);
      }else if(!this.multiple) this.resource = bindings.model.currentValue;
      else if(this.selected) this.selected = bindings.model.currentValue;
    }
  }

  isHref(str){
    const href = new RegExp('\/([a-zA-Z]+)\/([a-zA-Z0-9-]+)', 'gi');
    return angular.isString(str) && href.test(str);
  }

  onSelectEmitter($item, $select){
    if(this.clearOnSelect) $select.selected = null;
    if($item){
      const items = this.multiple ? this.selected : this.resource;
      this.model = items;
      this.$scope.$emit(onSelectEvent, $item, items);
      this.onChangeEmitter(items);
    }else{
      this.onRemoveEmitter($item);
    }
  }

  onRemoveEmitter($item){
    const items = this.multiple ? this.selected : this.resource;
    this.model = items;
    this.$scope.$emit(onRemoveEvent, $item, items);
    this.onChangeEmitter(items);
  }

  onChangeEmitter(item){
    this.$scope.$emit(onChangeEvent, item);
    this.model = item;
    if(this.onChange) this.onChange({option: item});
  }

  title(item) {
    return item && this.displayFunction ? this.displayFunction( item ).replace(/(<([^>]+)>)/gi, "") : '';
  }

  async filterAsync(input){
    if (this.httpRequestCanceler) {
      //Trying to cancel the previous http request, before we send a new one..."
      this.httpRequestCanceler.resolve(); // Aborts the previous $http request if it isn't finished.
    }

    if (input.length >= this.minimumInputLength) {

      this.$scope.$emit(onSearchEvent, input);

      let params = this.params || {};
      params[this.queryParam] = this.separator ? input.split(' ').join(this.separator) : input;
      params.limit = this.limit;

      if (this.localData) {
        this.similarResources = this.filterLocalDataFn ? this.filterLocalDataFn(this.localData, input) : this.localData;
      } else {
        this.httpRequestCanceler = this.$q.defer();

        try {
          let results = [];
          if (this.rawResults) {
            results = await this.sriClient.getRaw(this.resourceListUrl, params, { cancelPromise: this.httpRequestCanceler.promise });
            results = results.results.map(item => item.$$expanded || item);
          } else {
            results = await this.sriClient.getList(this.resourceListUrl, params, { cancelPromise: this.httpRequestCanceler.promise, inBatch: this.inBatch });
          }

          this.similarResources = this.filterApiDataFn ? this.filterApiDataFn(results) : results;
          this.$scope.$apply();
          this.$scope.$emit(onFetchEvent, this.similarResources);
        } catch (error) {
          if (error.status) {
            console.error('Api error with status ' + error.status, error);
            this.$scope.$emit(onFetchErrorEvent, error);
          } else {
            console.error('An unexpected error occured, when fetching data from the Api', error);
          }
        }
      }
    }
  }

}

module.exports = {
  template: require('./kovResourcePicker.html'),
  bindings: {
    resourceListUrl: '@url',
    params: '<parameters',
    queryParam: '@',
    separator: '@',
    display: '<',
    placeholder: '@',
    model: '<',
    name: '<',
    minimumInputLength: '<',
    limit: '<',
    multiple: '<',
    optionsTemplateUrl: '<',
    localData: '<',
    filterLocalDataFn: '<',
    filterApiDataFn: '<',
    disabled: '<',
    clearOnSelect: '<',
    allowClear: '@?',
    onChange: '&',
    api: '=',
    inBatch: '@',
    rawResults: '@?',
    sriClient: '<?'
  },
  controllerAs: 'ctrl',
  controller: ResourcePicker
};
